import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ConfirmDialog } from '../components/ConfirmDialog'
import {
  copyOrderBlankText,
  downloadOrderBlankPdf,
  downloadOrderBlankSpreadsheet,
  parseCartUnitPrice,
} from '../api/orderBlank'
import { saveOrderBlank } from '../api/orderBlankHistory'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { useAuth } from '../auth/AuthProvider'
import { buildOrderBlankFilename } from '../utils/orderBlankFilename'
import {
  describeMinOrderState,
  formatRubles,
  resolveMinOrderState,
} from '../components/suppliers/minOrderAmount'
import {
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/restaurantMyPrices'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { cartLineKey } from '../cart/cartLineKey'
import { useCart } from '../cart/CartProvider'
import { groupCartLines } from '../cart/groupCartLines'
import type { CartSupplierGroup } from '../cart/types'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { CartQuantityStepper } from '../components/CartQuantityStepper'
import { DataTable } from '../components/DataTable'

const CartLinesTable = ({
  lines,
  onUpdateQuantity,
  onRemoveLine,
}: {
  lines: ReturnType<typeof useCart>['lines']
  onUpdateQuantity: (key: string, quantity: number) => void
  onRemoveLine: (key: string) => void
}) => (
  <DataTable
    wrapClassName="market-table-wrap--cards"
    className="market-table--cards"
    caption="Позиции в заявке"
  >
    <thead>
      <tr>
        <th>Позиция</th>
        <th>Цена</th>
        <th>Ед.</th>
        <th>Поставщик</th>
        <th>Бренд</th>
        <th>Категория</th>
        <th>Кол-во</th>
        <th />
      </tr>
    </thead>
    <tbody>
      {lines.map((line) => {
        const key = cartLineKey(line)
        return (
          <tr key={key}>
            <td className="market-table__cell--primary" data-label="Позиция">
              {line.itemName}
            </td>
            <td className="market-table__cell--price" data-label="Цена">
              {line.price ?? '—'}
            </td>
            <td data-label="Ед.">{formatUnitLabel(line.unit)}</td>
            <td data-label="Поставщик">{line.supplierOrganizationName}</td>
            <td data-label="Бренд">{line.brand ?? '—'}</td>
            <td data-label="Категория">{line.productTypeName}</td>
            <td data-label="Кол-во">
              <CartQuantityStepper
                value={line.quantity}
                onChange={(quantity) => onUpdateQuantity(key, quantity)}
                onRemove={() => onRemoveLine(key)}
              />
            </td>
            <td className="market-table__cell--action" data-label="">
              <button
                type="button"
                className="echat-btn echat-btn--danger-soft echat-btn--sm market-table__row-action"
                onClick={() => onRemoveLine(key)}
              >
                Удалить
              </button>
            </td>
          </tr>
        )
      })}
    </tbody>
  </DataTable>
)

const ExternalSupplierActions = ({
  group,
  organizationId,
  restaurantName,
}: {
  group: CartSupplierGroup
  organizationId: number
  restaurantName: string
}) => {
  const [loading, setLoading] = useState<'pdf' | 'xlsx' | 'text' | 'save' | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  if (group.privateSupplierId == null) {
    return null
  }

  const privateSupplierId = group.privateSupplierId

  const blankRequest = {
    organizationId,
    privateSupplierId,
    lines: group.categories.flatMap((category) =>
      category.lines.map((line) => ({
        catalogItemId: line.catalogItemId,
        quantity: line.quantity,
        unit: formatUnitLabel(line.unit),
        unitPrice: parseCartUnitPrice(line.price),
        ...(line.note.trim() ? { note: line.note.trim() } : {}),
      })),
    ),
  }

  const handleSave = async () => {
    setLoading('save')
    setMessage(null)
    try {
      await saveOrderBlank(blankRequest)
      setMessage('Заявка сохранена в историю — можно открыть снова из вкладки «История»')
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось сохранить заявку'))
    } finally {
      setLoading(null)
    }
  }

  const handlePdf = async () => {
    setLoading('pdf')
    setMessage(null)
    try {
      const blob = await downloadOrderBlankPdf(blankRequest)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = buildOrderBlankFilename(restaurantName, organizationId)
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось сформировать PDF'))
    } finally {
      setLoading(null)
    }
  }

  const handleSpreadsheet = async () => {
    setLoading('xlsx')
    setMessage(null)
    try {
      const blob = await downloadOrderBlankSpreadsheet(blankRequest)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = buildOrderBlankFilename(restaurantName, organizationId, 'xlsx')
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось сформировать таблицу'))
    } finally {
      setLoading(null)
    }
  }

  const handleCopy = async () => {
    setLoading('text')
    setMessage(null)
    try {
      await copyOrderBlankText(blankRequest)
      setMessage('Текст скопирован в буфер обмена')
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось скопировать текст'))
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="market-cart-external-actions">
      <div className="market-cart-external-actions__buttons">
        <button
          type="button"
          className="echat-btn echat-btn--primary echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handleSave()}
        >
          {loading === 'save' ? 'Сохраняем…' : 'Сохранить заявку'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handlePdf()}
        >
          {loading === 'pdf' ? 'Формируем…' : 'Сформировать бланк заказа'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handleSpreadsheet()}
        >
          {loading === 'xlsx' ? 'Формируем…' : 'Скачать таблицей'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handleCopy()}
        >
          {loading === 'text' ? 'Копируем…' : 'Скопировать текст'}
        </button>
      </div>
      {message ? (
        <p className="market-cart-external-actions__message" role="status">
          {message}
        </p>
      ) : null}
    </div>
  )
}

/**
 * Порог поставщика над списком позиций.
 *
 * Показывается там, где на него ещё можно повлиять — рядом с позициями этого поставщика,
 * а не у общей кнопки внизу: добирать придётся именно в этой группе.
 *
 * Формирование бланка не блокируется. Бланк не уходит поставщику сам — его забирает
 * пользователь, и запрещать ему скачать собственный файл неправильно, тем более что
 * порог мог устареть.
 */
const SupplierMinOrderNotice = ({
  group,
  supplier,
}: {
  group: CartSupplierGroup
  supplier: RestaurantPrivateSupplierDto | undefined
}) => {
  const groupTotal = useMemo(
    () =>
      group.categories
        .flatMap((category) => category.lines)
        .reduce((sum, line) => {
          const price = parseCartUnitPrice(line.price)
          return price == null ? sum : sum + price * line.quantity
        }, 0),
    [group],
  )

  const state = resolveMinOrderState(supplier, groupTotal)
  const message = describeMinOrderState(state)

  return (
    <div className="market-cart-external-group__minimum">
      <span className="market-muted">Сумма по поставщику: {formatRubles(groupTotal)}</span>
      {message ? (
        <p
          className={
            state.kind === 'short'
              ? 'market-cart-external-group__minimum-warning'
              : 'market-muted'
          }
          role={state.kind === 'short' ? 'status' : undefined}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}

/** Черновик заявки внутри «Заявки»: позиции из «Моих прайсов», сгруппированные по поставщику. */
export const CartDraftPanel = () => {
  const navigate = useNavigate()
  const { session, organizationName } = useAuth()
  const { lines, lineCount, updateQuantity, removeLine, clearCart } = useCart()
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const grouped = useMemo(() => groupCartLines(lines), [lines])

  const totalQuantity = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity, 0),
    [lines],
  )

  const organizationId = session?.organizationId ?? 0
  // Пороги живут в карточках поставщиков, а не в строках корзины: строка знает цену, но
  // не знает, от какой суммы поставщик везёт.
  const [suppliersById, setSuppliersById] = useState<Map<number, RestaurantPrivateSupplierDto>>(
    () => new Map(),
  )
  useEffect(() => {
    let cancelled = false
    void listRestaurantPrivateSuppliers(organizationId)
      .then((suppliers) => {
        if (!cancelled) {
          setSuppliersById(new Map(suppliers.map((supplier) => [supplier.id, supplier])))
        }
      })
      // Порог — подсказка, а не условие работы: не смогли загрузить — просто не подскажем.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [organizationId])

  if (lineCount === 0) {
    return (
      <div>
        <p className="echat-muted market-request-draft-lead">
          Соберите позиции из «Моих прайсов» и сформируйте бланк для поставщика.
        </p>
        <MarketEmptyState
          title="Черновик заявки пуст"
          description="Добавьте позиции из «Моих прайсов», чтобы сформировать заявку."
          actions={[{ label: 'Открыть мои прайсы', onClick: () => navigate('/my-prices') }]}
        />
      </div>
    )
  }

  return (
    <div className="market-page--with-action-bar">
      <ConfirmDialog
        open={showClearConfirm}
        title="Очистить корзину"
        message="Все позиции будут удалены. Это действие нельзя отменить."
        confirmLabel="Очистить"
        cancelLabel="Отмена"
        onConfirm={() => {
          clearCart()
          setShowClearConfirm(false)
        }}
        onCancel={() => setShowClearConfirm(false)}
      />
      <div className="market-request-draft-toolbar">
        <p className="echat-muted">
          {lineCount} поз. · {totalQuantity} ед. суммарно
        </p>
        <div className="market-request-draft-toolbar__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost echat-btn--sm"
            onClick={() => navigate('/my-prices')}
          >
            В мои прайсы
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--ghost echat-btn--sm"
            onClick={() => setShowClearConfirm(true)}
          >
            Очистить
          </button>
        </div>
      </div>

      {grouped.external.length > 0 ? (
        <>
          <p className="market-muted">
            Позиции из «Моих прайсов» — сформируйте бланк заказа и отправьте поставщику вручную.
          </p>
          {grouped.external.map((group) => (
            <div
              key={group.privateSupplierId ?? group.supplierOrganizationName}
              className="market-cart-external-group"
            >
              <div className="market-cart-external-group__header">
                <strong>{group.supplierOrganizationName}</strong>
                <ExternalSupplierActions
                  group={group}
                  organizationId={organizationId}
                  restaurantName={organizationName ?? 'restoran'}
                />
              </div>
              <SupplierMinOrderNotice
                group={group}
                supplier={
                  group.privateSupplierId != null
                    ? suppliersById.get(group.privateSupplierId)
                    : undefined
                }
              />
              <CartLinesTable
                lines={group.categories.flatMap((category) => category.lines)}
                onUpdateQuantity={updateQuantity}
                onRemoveLine={removeLine}
              />
            </div>
          ))}
        </>
      ) : null}

      <div className="market-action-bar" role="region" aria-label="Действия с заявкой">
        <span className="market-action-bar__summary">
          {lineCount} поз. · {totalQuantity} ед.
        </span>
        <div className="market-action-bar__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            onClick={() => navigate('/my-prices')}
          >
            В мои прайсы
          </button>
        </div>
      </div>
    </div>
  )
}
